import { useState, useCallback, useEffect } from "react";
import {
  Card,
  Button,
  Space,
  Typography,
  Table,
  message,
  App,
  Row,
  Col,
  Spin,
  Select,
  Form,
  Empty,
  Tag,
  Upload,
  Divider,
} from "antd";
import {
  FolderOutlined,
  FileOutlined,
  ReloadOutlined,
  PlayCircleOutlined,
  DownloadOutlined,
  UnorderedListOutlined,
  ThunderboltOutlined,
  InboxOutlined,
} from "@ant-design/icons";
import axiosInstance from "../utils/axiosConfig";
import DashboardLayout from "./DashboardLayout";

const { Title, Text } = Typography;
const { Dragger } = Upload;

const BUCKET_DISPLAY =
  import.meta.env.VITE_S3_BUCKET || "verifyu-phonepe-processing";

function getErrorMessage(error, fallback = "Request failed") {
  const d = error.response?.data?.detail;
  const msg =
    error.response?.data?.message ??
    error.response?.data?.error ??
    error.message;
  if (d !== undefined && d !== null) {
    return Array.isArray(d) ? d.map((e) => e?.msg ?? JSON.stringify(e)).join(", ") : String(d);
  }
  return msg || fallback;
}

export default function S3FileManager() {
  const { message: messageApi } = App.useApp();

  const [fileList, setFileList] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [processLoading, setProcessLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [listType, setListType] = useState(undefined); // undefined = all, or 'signzy' | 'surepass'
  const [listViewMode, setListViewMode] = useState("output"); // 'output' | 'logs'
  const [selectedOutputRowKeys, setSelectedOutputRowKeys] = useState([]);
  const [downloadingFileKey, setDownloadingFileKey] = useState(null); // single-file download loading
  const [uploadSuccessPath, setUploadSuccessPath] = useState(null); // set after upload so Process is enabled
  const [uploadSuccessFileName, setUploadSuccessFileName] = useState(null); // display name for the file ready to process
  const [inputFileList, setInputFileList] = useState([]);
  const [inputListLoading, setInputListLoading] = useState(false);
  const [processingInputKey, setProcessingInputKey] = useState(null); // which input file is being processed

  const fetchFiles = useCallback(
    async (refresh = false) => {
      setListLoading(true);
      try {
        const params = { refresh };
        if (listType) params.type = listType;
        const response = await axiosInstance.get("/api/phonePe/s3/list", {
          params,
        });
        const data = response.data;
        const items =
          data?.files ?? data?.items ?? (Array.isArray(data) ? data : []);
        setFileList(Array.isArray(items) ? items : []);
        messageApi.success("File list refreshed", 2);
      } catch (error) {
        console.error("List S3 files error:", error);
        messageApi.error(getErrorMessage(error, "Failed to fetch file list"), 5);
        setFileList([]);
      } finally {
        setListLoading(false);
      }
    },
    [listType, messageApi]
  );

  useEffect(() => {
    fetchFiles(false);
  }, []);

  const fetchInputFiles = useCallback(async () => {
    setInputListLoading(true);
    try {
      const response = await axiosInstance.get("/api/phonePe/s3/input/list");
      const data = response.data;
      const items =
        data?.files ?? data?.items ?? (Array.isArray(data) ? data : []);
      setInputFileList(Array.isArray(items) ? items : []);
      messageApi.success("Input file list refreshed", 2);
    } catch (error) {
      console.error("List input files error:", error);
      messageApi.error(
        getErrorMessage(error, "Failed to fetch input file list"),
        4
      );
      setInputFileList([]);
    } finally {
      setInputListLoading(false);
    }
  }, [messageApi]);

  useEffect(() => {
    fetchInputFiles();
  }, [fetchInputFiles]);

  const [uploadFileList, setUploadFileList] = useState([]);
  const [logsDownloadLoading, setLogsDownloadLoading] = useState(false);

  const handleUploadToS3 = async () => {
    const entry = uploadFileList[0];
    const rawFile = entry?.originFileObj ?? entry;
    if (!rawFile || !(rawFile instanceof File)) {
      messageApi.warning("Please select a file to upload");
      return;
    }
    setUploadLoading(true);
    setUploadSuccessPath(null);
    setUploadSuccessFileName(null);
    try {
      const formData = new FormData();
      formData.append("file", rawFile);
      const response = await axiosInstance.post(
        "/api/phonePe/s3/upload",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      const msg = response.data?.message ?? "File uploaded successfully";
      messageApi.success(msg, 3);
      const path =
        response.data?.folder_path ??
        response.data?.key ??
        response.data?.path ??
        response.data?.filename ??
        rawFile.name;
      const displayName = typeof path === "string" ? path.split("/").pop() || path : rawFile.name;
      setUploadSuccessPath(path);
      setUploadSuccessFileName(displayName || rawFile.name);
      setUploadFileList([]);
      fetchFiles(true);
      fetchInputFiles();
    } catch (error) {
      console.error("Upload error:", error);
      messageApi.error(getErrorMessage(error, "Upload failed"), 5);
    } finally {
      setUploadLoading(false);
    }
  };

  const handleRunProcessing = async () => {
    if (!uploadSuccessPath) return;
    setProcessLoading(true);
    try {
      const response = await axiosInstance.post("/api/phonePe/folder", {
        folder_path: uploadSuccessPath,
      });
      const msg = response.data?.message ?? "PhonePe data processing completed";
      messageApi.success(msg, 4);
      fetchFiles(true);
      fetchInputFiles();
    } catch (error) {
      console.error("Process error:", error);
      messageApi.error(getErrorMessage(error, "Processing failed"), 5);
    } finally {
      setProcessLoading(false);
    }
  };

  const handleProcessInputFile = async (pathOrKey) => {
    const folder_path =
      pathOrKey?.key ?? pathOrKey?.path ?? pathOrKey?.name ?? pathOrKey;
    if (!folder_path) return;
    setProcessingInputKey(folder_path);
    try {
      const response = await axiosInstance.post("/api/phonePe/folder", {
        folder_path,
      });
      const msg = response.data?.message ?? "PhonePe data processing completed";
      messageApi.success(msg, 4);
      fetchFiles(true);
      fetchInputFiles();
    } catch (error) {
      console.error("Process input file error:", error);
      messageApi.error(getErrorMessage(error, "Processing failed"), 5);
    } finally {
      setProcessingInputKey(null);
    }
  };

  const handleDownloadOutput = async (keys) => {
    setDownloadLoading(true);
    try {
      const body = { folder_path: "downloads" };
      const keysToUse = Array.isArray(keys) && keys.length > 0 ? keys : undefined;
      if (keysToUse?.length) body.files = keysToUse;
      const response = await axiosInstance.post(
        "/api/phonePe/s3/output/download",
        body
      );
      const msg = response.data?.message ?? "Output files downloaded successfully";
      messageApi.success(msg, 4);
      setSelectedOutputRowKeys([]);
    } catch (error) {
      console.error("Download output error:", error);
      messageApi.error(getErrorMessage(error, "Download failed"), 5);
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleDownloadSingleFile = async (rowKey) => {
    setDownloadingFileKey(rowKey);
    try {
      const response = await axiosInstance.post(
        "/api/phonePe/s3/output/download",
        { folder_path: "downloads", files: [rowKey] }
      );
      const msg = response.data?.message ?? "File downloaded successfully";
      messageApi.success(msg, 3);
    } catch (error) {
      messageApi.error(getErrorMessage(error, "Download failed"), 4);
    } finally {
      setDownloadingFileKey(null);
    }
  };

  const handleDownloadLogs = async () => {
    setLogsDownloadLoading(true);
    try {
      const response = await axiosInstance.post(
        "/api/phonePe/s3/logs/download",
        { folder_path: "downloads" }
      );
      const msg = response.data?.message ?? "Logs downloaded successfully";
      messageApi.success(msg, 4);
    } catch (error) {
      console.error("Download logs error:", error);
      messageApi.error(getErrorMessage(error, "Download failed"), 5);
    } finally {
      setLogsDownloadLoading(false);
    }
  };

  const getFileKey = (record) =>
    record.key ?? record.name ?? record.filename ?? record.Key;

  const outputColumns = [
    {
      title: "File",
      dataIndex: "key",
      key: "key",
      ellipsis: true,
      render: (key, record) => (
        <Space>
          <FileOutlined style={{ color: "#0066ff" }} />
          <Text strong copyable={{ text: getFileKey(record) }}>
            {getFileKey(record) ?? "—"}
          </Text>
        </Space>
      ),
    },
    {
      title: "Size",
      key: "size",
      width: 120,
      render: (_, record) => {
        const size =
          record.size ?? record.size_bytes ?? record.Size;
        if (size == null) return "—";
        return size < 1024
          ? `${size} B`
          : size < 1024 * 1024
            ? `${(size / 1024).toFixed(1)} KB`
            : `${(size / (1024 * 1024)).toFixed(2)} MB`;
      },
    },
    {
      title: "Last modified",
      key: "lastModified",
      width: 180,
      render: (_, record) => {
        const t =
          record.lastModified ??
          record.last_modified ??
          record.LastModified ??
          record.Modified;
        if (!t) return "—";
        try {
          return new Date(t).toLocaleString(undefined, {
            dateStyle: "short",
            timeStyle: "short",
          });
        } catch {
          return "—";
        }
      },
    },
    {
      title: "Action",
      key: "action",
      width: 110,
      render: (_, record) => {
        const rowKey = getFileKey(record);
        const loading = downloadingFileKey === rowKey;
        return (
          <Button
            type="primary"
            size="small"
            icon={<DownloadOutlined />}
            loading={loading}
            onClick={() => handleDownloadSingleFile(rowKey)}
            style={{
              background:
                "linear-gradient(135deg, #722ed1 0%, #531dab 100%)",
              border: "none",
              borderRadius: 8,
            }}
          >
            Download
          </Button>
        );
      },
    },
  ];

  const listTableDataSource = fileList.map((item, idx) => ({
    ...item,
    key: item.key ?? item.name ?? item.filename ?? idx,
  }));

  return (
    <DashboardLayout title="PhonePe S3 / File Manager">
      <Card
        style={{
          marginBottom: 24,
          borderRadius: 16,
          background: "linear-gradient(135deg, #0066ff 0%, #0052cc 100%)",
          border: "none",
          boxShadow: "0 8px 24px rgba(0, 102, 255, 0.2)",
        }}
        styles={{ body: { padding: "24px 32px" } }}
      >
        <Row align="middle" gutter={24}>
          <Col flex="auto">
            <Space size="large">
              <div
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: 12,
                  background: "rgba(255, 255, 255, 0.2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backdropFilter: "blur(10px)",
                }}
              >
                <FolderOutlined style={{ fontSize: 28, color: "#ffffff" }} />
              </div>
              <div>
                <Title
                  level={3}
                  style={{
                    margin: 0,
                    color: "#ffffff",
                    fontWeight: 700,
                    marginBottom: 4,
                  }}
                >
                  PhonePe S3 / File Manager
                </Title>
                <Text
                  style={{ color: "rgba(255, 255, 255, 0.9)", fontSize: 14 }}
                >
                  Process folder, upload to S3, list output files, download output or logs
                </Text>
              </div>
            </Space>
          </Col>
          <Col>
            <Tag
              icon={<FolderOutlined />}
              color="rgba(255, 255, 255, 0.3)"
              style={{
                color: "#ffffff",
                border: "1px solid rgba(255, 255, 255, 0.3)",
                padding: "8px 16px",
                borderRadius: 8,
                fontSize: 14,
                fontWeight: 600,
              }}
            >
              {BUCKET_DISPLAY}
            </Tag>
          </Col>
        </Row>
      </Card>

      <Row gutter={[24, 24]} style={{ alignItems: "stretch" }}>
        {/* Upload & process: one file → Upload to S3 → Run processing (no path input) */}
        <Col xs={24} lg={12} style={{ display: "flex" }}>
          <Card
            style={{
              width: "100%",
              flex: 1,
              display: "flex",
              flexDirection: "column",
              borderRadius: 16,
              boxShadow: "0 4px 20px rgba(0, 102, 255, 0.1)",
              border: "1px solid #e1e8f0",
            }}
            styles={{ body: { flex: 1, display: "flex", flexDirection: "column", padding: 24 } }}
            title={
              <Space>
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 10,
                    background:
                      "linear-gradient(135deg, #52c41a 0%, #389e0d 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <InboxOutlined style={{ fontSize: 20, color: "#ffffff" }} />
                </div>
                <Title level={4} style={{ margin: 0 }}>
                  Upload & process
                </Title>
              </Space>
            }
          >
            <Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
              Select a file, upload it to S3, then run PhonePe processing on it. No path input needed.
            </Text>
            <Form layout="vertical">
              <Form.Item label="File">
                <Dragger
                  name="file"
                  multiple={false}
                  fileList={uploadFileList}
                  beforeUpload={(file) => {
                    setUploadSuccessPath(null);
                    setUploadSuccessFileName(null);
                    setUploadFileList([
                      {
                        uid: file.uid ?? "-1",
                        name: file.name,
                        status: "done",
                        originFileObj: file,
                      },
                    ]);
                    return false;
                  }}
                  onRemove={() => setUploadFileList([])}
                  maxCount={1}
                >
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined style={{ fontSize: 48, color: "#52c41a" }} />
                  </p>
                  <p className="ant-upload-text">
                    Click or drag a file here
                  </p>
                </Dragger>
              </Form.Item>
              {uploadSuccessPath && uploadSuccessFileName && (
                <div
                  style={{
                    marginBottom: 16,
                    padding: "12px 16px",
                    background: "linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%)",
                    borderRadius: 10,
                    border: "1px solid #b7eb8f",
                  }}
                >
                  <Text strong style={{ color: "#389e0d" }}>
                    File ready to process:
                  </Text>{" "}
                  <Text copyable={{ text: uploadSuccessPath }} style={{ fontWeight: 600 }}>
                    {uploadSuccessFileName}
                  </Text>
                </div>
              )}
              <Space wrap size="middle">
                <Button
                  type="primary"
                  icon={<InboxOutlined />}
                  loading={uploadLoading}
                  size="large"
                  onClick={handleUploadToS3}
                  disabled={!uploadFileList.length}
                  style={{
                    background:
                      "linear-gradient(135deg, #13c2c2 0%, #08979c 100%)",
                    border: "none",
                    borderRadius: 10,
                    fontWeight: 600,
                  }}
                >
                  Upload to S3
                </Button>
                <Button
                  type="primary"
                  icon={<PlayCircleOutlined />}
                  loading={processLoading}
                  size="large"
                  onClick={handleRunProcessing}
                  disabled={!uploadSuccessPath}
                  style={{
                    background:
                      "linear-gradient(135deg, #52c41a 0%, #389e0d 100%)",
                    border: "none",
                    borderRadius: 10,
                    fontWeight: 600,
                  }}
                >
                  Run processing
                </Button>
              </Space>
            </Form>
          </Card>
        </Col>

        {/* Input files in S3: list with Process button per file */}
        <Col xs={24} lg={12} style={{ display: "flex" }}>
          <Card
            style={{
              width: "100%",
              flex: 1,
              display: "flex",
              flexDirection: "column",
              borderRadius: 16,
              boxShadow: "0 4px 20px rgba(0, 102, 255, 0.1)",
              border: "1px solid #e1e8f0",
            }}
            styles={{ body: { flex: 1, display: "flex", flexDirection: "column", padding: 24 } }}
            title={
              <Space>
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 10,
                    background:
                      "linear-gradient(135deg, #fa8c16 0%, #d46b08 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FileOutlined style={{ fontSize: 20, color: "#ffffff" }} />
                </div>
                <Title level={4} style={{ margin: 0 }}>
                  Input files (S3)
                </Title>
                {inputFileList.length > 0 && (
                  <Tag
                    color="orange"
                    style={{
                      fontSize: 14,
                      padding: "4px 12px",
                      borderRadius: 8,
                      fontWeight: 600,
                    }}
                  >
                    {inputFileList.length} file(s)
                  </Tag>
                )}
              </Space>
            }
          >
            <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
              <Button
                type="default"
                icon={<ReloadOutlined />}
                onClick={fetchInputFiles}
                loading={inputListLoading}
                style={{ borderRadius: 8 }}
              >
                Refresh list
              </Button>
              {inputListLoading ? (
                <div style={{ textAlign: "center", padding: 24 }}>
                  <Spin size="large" />
                </div>
              ) : inputFileList.length === 0 ? (
                <Empty
                  description="No input files in S3. Upload a file first."
                  style={{ padding: 24 }}
                />
              ) : (
                <Table
                  dataSource={inputFileList.map((item, idx) => ({
                    ...item,
                    _key: item.key ?? item.name ?? item.path ?? item.filename ?? idx,
                  }))}
                  rowKey="_key"
                  pagination={false}
                  size="small"
                  columns={[
                    {
                      title: "File",
                      dataIndex: "key",
                      key: "key",
                      ellipsis: true,
                      render: (key, record) => (
                        <Text strong copyable={{ text: key ?? record?.name ?? record?.path ?? key }}>
                          {key ?? record?.name ?? record?.path ?? "—"}
                        </Text>
                      ),
                    },
                    {
                      title: "Action",
                      key: "action",
                      width: 120,
                      render: (_, record) => {
                        const path =
                          record.key ?? record.path ?? record.name ?? record.filename;
                        const loading = processingInputKey === path;
                        return (
                          <Button
                            type="primary"
                            size="small"
                            icon={<PlayCircleOutlined />}
                            loading={loading}
                            onClick={() => handleProcessInputFile(record)}
                            style={{
                              background:
                                "linear-gradient(135deg, #52c41a 0%, #389e0d 100%)",
                              border: "none",
                              borderRadius: 8,
                            }}
                          >
                            Process
                          </Button>
                        );
                      },
                    },
                  ]}
                />
              )}
            </Space>
          </Card>
        </Col>

        {/* Browse & download: full-width card with Output/Logs switch and download */}
        <Col xs={24}>
          <Card
            title={
              <Space>
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 10,
                    background:
                      "linear-gradient(135deg, #00d9ff 0%, #0066ff 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <UnorderedListOutlined
                    style={{ fontSize: 20, color: "#ffffff" }}
                  />
                </div>
                <Title level={4} style={{ margin: 0 }}>
                  Browse & download
                </Title>
                {listViewMode === "output" && fileList.length > 0 && (
                  <Tag
                    color="blue"
                    style={{
                      fontSize: 14,
                      padding: "4px 12px",
                      borderRadius: 8,
                      fontWeight: 600,
                    }}
                  >
                    {fileList.length} file(s)
                  </Tag>
                )}
              </Space>
            }
            style={{
              borderRadius: 16,
              boxShadow: "0 4px 20px rgba(0, 102, 255, 0.1)",
              border: "1px solid #e1e8f0",
            }}
            styles={{ body: { padding: 24 } }}
          >
            <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
              <Space wrap align="center" style={{ width: "100%", justifyContent: "space-between" }}>
                <Space wrap align="center">
                  <Text strong>Content:</Text>
                  <Select
                    value={listViewMode}
                    onChange={setListViewMode}
                    style={{ minWidth: 120, borderRadius: 8 }}
                    options={[
                      { value: "output", label: "Output" },
                      { value: "logs", label: "Logs" },
                    ]}
                  />
                  {listViewMode === "output" && (
                    <>
                      <Text strong style={{ marginLeft: 8 }}>Type:</Text>
                      <Select
                        placeholder="All"
                        allowClear
                        value={listType}
                        onChange={setListType}
                        style={{ minWidth: 120, borderRadius: 8 }}
                        options={[
                          { value: undefined, label: "All" },
                          { value: "signzy", label: "Signzy" },
                          { value: "surepass", label: "Surepass" },
                        ]}
                      />
                      <Button
                        type="primary"
                        icon={<ReloadOutlined />}
                        onClick={() => fetchFiles(true)}
                        loading={listLoading}
                        style={{
                          background:
                            "linear-gradient(135deg, #0066ff 0%, #0052cc 100%)",
                          border: "none",
                          borderRadius: 8,
                        }}
                      >
                        Refresh
                      </Button>
                      <Button
                        type="primary"
                        icon={<DownloadOutlined />}
                        loading={downloadLoading}
                        onClick={() =>
                          handleDownloadOutput(
                            selectedOutputRowKeys.length > 0
                              ? selectedOutputRowKeys
                              : listTableDataSource.map((r) => getFileKey(r))
                          )
                        }
                        disabled={listTableDataSource.length === 0}
                        style={{
                          background:
                            "linear-gradient(135deg, #722ed1 0%, #531dab 100%)",
                          border: "none",
                          borderRadius: 8,
                          fontWeight: 600,
                        }}
                      >
                        Download
                        {selectedOutputRowKeys.length > 0
                          ? ` (${selectedOutputRowKeys.length})`
                          : ""}
                      </Button>
                    </>
                  )}
                </Space>
              </Space>

              {listViewMode === "output" && (
                <>
                  {listLoading ? (
                    <div style={{ textAlign: "center", padding: 32 }}>
                      <Spin size="large" />
                    </div>
                  ) : fileList.length === 0 ? (
                    <Empty
                      description="No files yet. Refresh or run a folder process."
                      style={{ padding: 32 }}
                    >
                      <Button
                        type="primary"
                        icon={<ReloadOutlined />}
                        onClick={() => fetchFiles(true)}
                        style={{
                          background:
                            "linear-gradient(135deg, #0066ff 0%, #0052cc 100%)",
                          border: "none",
                        }}
                      >
                        Refresh
                      </Button>
                    </Empty>
                  ) : (
                    <Table
                      rowSelection={{
                        selectedRowKeys: selectedOutputRowKeys,
                        onChange: setSelectedOutputRowKeys,
                      }}
                      columns={outputColumns}
                      dataSource={listTableDataSource}
                      rowKey={(r) => getFileKey(r) ?? r.key}
                      pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        showTotal: (total) => `Total ${total} files`,
                      }}
                      scroll={{ x: "max-content" }}
                    />
                  )}
                </>
              )}

              {listViewMode === "logs" && (
                <>
                  <Text type="secondary">
                    Download logs from S3 to the server (default folder).
                  </Text>
                  <Button
                    type="primary"
                    icon={<DownloadOutlined />}
                    loading={logsDownloadLoading}
                    onClick={handleDownloadLogs}
                    size="large"
                    style={{
                      background:
                        "linear-gradient(135deg, #722ed1 0%, #531dab 100%)",
                      border: "none",
                      borderRadius: 10,
                      fontWeight: 600,
                    }}
                  >
                    Download logs
                  </Button>
                </>
              )}
            </Space>
          </Card>
        </Col>
      </Row>
    </DashboardLayout>
  );
}
